import * as React from 'react';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { ShapeData } from "../services/shape-service";
import { XSelector, YSelector } from "../reducers";

interface Props {
    readonly dragging: boolean,
    readonly resizing: boolean,
    readonly shape: ShapeData,
    readonly selected: boolean,
    readonly stopPropagation: (e: React.MouseEvent<SVGElement>) => void,
    readonly onTextShapeDoubleClicked: (e: React.MouseEvent<SVGElement>) => void,
    readonly onStartDragging: (e: React.MouseEvent<SVGElement>) => void,
    readonly onStartSelectorDragging: (xSelector: XSelector, ySelector: YSelector, e: React.MouseEvent<SVGElement>) => void,
    readonly onDeleteButtonClicked: (e: React.MouseEvent<SVGElement>) => void,
}

const STROKE_WIDTH = 6;
const HOTSTPOT_STROKE_WIDTH = 30;
const SELECTOR_SIZE = 8;
const SELECTOR_HOTSPOT_SIZE = 40;

const styles = (theme: Theme) => createStyles({
    shape: {
        fill: 'none',
        strokeWidth: STROKE_WIDTH,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        stroke: '#0a0a0a',
    },
    text: {
        fill: theme.palette.common.black,
        fontFamily: '"Gloria Hallelujah", cursive',
        fontWeight: 'bold',
        textAnchor: 'start',
        userSelect: 'none',
        '&:selection': {
            background: 'none'
        },
        whiteSpace: 'pre-wrap',
        margin: 0,
        padding: 0,
    },
    tspan: {
        alignmentBaseline: 'alphabetic',
    },
    shapeHotspot: {
        stroke: 'transparent',
        strokeWidth: HOTSTPOT_STROKE_WIDTH,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
    },
    shapeHotspotNotSelected: {
        fill: 'none',
    },
    shapeHotspotSelected: {
        fill: 'transparent',
    },
    bounds: {
        stroke: 'black',
        strokeWidth: 1,
        strokeDasharray: '10 10',
        fill: 'none',
    },
    selector: {
        fill: 'black',
    },
    deleteButton: {
        fill: theme.palette.grey[600],
        '&:hover': {
            fill: theme.palette.grey[700]
        },
    },
});

type Styles =
    'shape'
    | 'text'
    | 'tspan'
    | 'shapeHotspot'
    | 'shapeHotspotNotSelected'
    | 'shapeHotspotSelected'
    | 'bounds'
    | 'selector'
    | 'deleteButton';


interface SelectorProps {
    readonly x: number,
    readonly y: number,
    readonly cursorClass: string,
    readonly stopPropagation: (event: React.MouseEvent<SVGElement>) => void,
    readonly onStartSelectorDragging: (event: React.MouseEvent<SVGElement>) => void,
}

const Selector = ({x, y, cursorClass, classes, stopPropagation, onStartSelectorDragging}: SelectorProps & WithStyles<Styles>) => (
    <g>
        <rect className={classes.selector}
              x={x - SELECTOR_SIZE / 2}
              y={y - SELECTOR_SIZE / 2}
              width={SELECTOR_SIZE}
              height={SELECTOR_SIZE}
        />
        <rect className={`${classes.shapeHotspotSelected} ${cursorClass}`}
              x={x - SELECTOR_HOTSPOT_SIZE / 2}
              y={y - SELECTOR_HOTSPOT_SIZE / 2}
              width={SELECTOR_HOTSPOT_SIZE}
              height={SELECTOR_HOTSPOT_SIZE}
              onMouseDown={onStartSelectorDragging}
              onClick={stopPropagation}
        />
    </g>
);

const ShapeView = ({dragging, resizing, shape, selected, classes, stopPropagation, onStartDragging, onStartSelectorDragging, onDeleteButtonClicked, onTextShapeDoubleClicked}: Props & WithStyles<Styles>) => {
    const cursorClass = dragging ? 'cursorGrabbing' : 'cursorGrab';
    const inverted = shape.x1 < shape.x2 !== shape.y1 < shape.y2;
    const width = Math.abs(shape.x2 - shape.x1);
    const height = Math.abs(shape.y2 - shape.y1);
    return (
        <g>
            {shape.type === 'TEXT' ?
                <g transform={`translate(${Math.min(shape.x1, shape.x2)} ${Math.min(shape.y1, shape.y2)})`}>
                    <text transform={`rotate(${shape.rotation})`}
                          style={{fontSize: shape.size, lineHeight: 1.5 * shape.size}}
                          y={-0.5 * shape.size}
                          pointerEvents="none"
                          className={classes.text}
                    >
                        {shape.lines.map((line, index) =>
                            <tspan key={index} className={classes.tspan} x={0} dy={1.5 * shape.size}>{line}</tspan>
                        )}
                    </text>
                    <rect x={0} y={0} width={width} height={height}
                          className={`${classes.shapeHotspot} ${classes.shapeHotspotSelected} ${cursorClass}`}
                          onMouseDown={onStartDragging}
                          onDoubleClick={onTextShapeDoubleClicked}
                    />
                </g>
                :
                <g transform={`translate(${shape.x1} ${shape.y1})`}>
                    <path className={classes.shape} d={shape.path} pointerEvents="none"/>
                    <path
                        className={`${classes.shapeHotspot} ${cursorClass} ${selected ? classes.shapeHotspotSelected : classes.shapeHotspotNotSelected}`}
                        d={shape.path}
                        onClick={stopPropagation}
                        onMouseDown={onStartDragging}
                    />
                </g>
            }

            {selected && !dragging &&
            <g transform={`translate(${shape.x1} ${shape.y1})`}>
                <rect className={classes.bounds}
                      x={Math.min(0, shape.x2 - shape.x1) - STROKE_WIDTH / 2}
                      y={Math.min(0, shape.y2 - shape.y1) - STROKE_WIDTH / 2}
                      width={Math.abs(shape.x2 - shape.x1) + STROKE_WIDTH}
                      height={Math.abs(shape.y2 - shape.y1) + STROKE_WIDTH}/>
                <Selector x={shape.x1 < shape.x2 ? -STROKE_WIDTH / 2 : STROKE_WIDTH / 2}
                          y={shape.y1 < shape.y2 ? -STROKE_WIDTH / 2 : STROKE_WIDTH / 2}
                          cursorClass={inverted ? 'cursorResizeLowerLeftUpperRight' : 'cursorResizeUpperLeftLowerRight'}
                          classes={classes}
                          stopPropagation={stopPropagation}
                          onStartSelectorDragging={e => onStartSelectorDragging(XSelector.x1, YSelector.y1, e)}
                />
                <Selector x={(shape.x2 - shape.x1) / 2}
                          y={shape.y1 < shape.y2 ? -STROKE_WIDTH / 2 : STROKE_WIDTH / 2}
                          cursorClass="cursorResizeUpDown"
                          classes={classes}
                          stopPropagation={stopPropagation}
                          onStartSelectorDragging={e => onStartSelectorDragging(XSelector.xm, YSelector.y1, e)}
                />
                <Selector x={shape.x2 - shape.x1 + (shape.x1 < shape.x2 ? STROKE_WIDTH / 2 : -STROKE_WIDTH / 2)}
                          y={shape.y1 < shape.y2 ? -STROKE_WIDTH / 2 : STROKE_WIDTH / 2}
                          cursorClass={inverted ? 'cursorResizeUpperLeftLowerRight' : 'cursorResizeLowerLeftUpperRight'}
                          classes={classes}
                          stopPropagation={stopPropagation}
                          onStartSelectorDragging={e => onStartSelectorDragging(XSelector.x2, YSelector.y1, e)}
                />
                <Selector x={shape.x2 - shape.x1 + (shape.x1 < shape.x2 ? STROKE_WIDTH / 2 : -STROKE_WIDTH / 2)}
                          y={(shape.y2 - shape.y1) / 2}
                          cursorClass="cursorResizeLeftRight"
                          classes={classes}
                          stopPropagation={stopPropagation}
                          onStartSelectorDragging={e => onStartSelectorDragging(XSelector.x2, YSelector.ym, e)}
                />
                <Selector x={shape.x2 - shape.x1 + (shape.x1 < shape.x2 ? STROKE_WIDTH / 2 : -STROKE_WIDTH / 2)}
                          y={shape.y2 - shape.y1 + (shape.y1 < shape.y2 ? STROKE_WIDTH / 2 : -STROKE_WIDTH / 2)}
                          cursorClass={inverted ? 'cursorResizeLowerLeftUpperRight' : 'cursorResizeUpperLeftLowerRight'}
                          classes={classes}
                          stopPropagation={stopPropagation}
                          onStartSelectorDragging={e => onStartSelectorDragging(XSelector.x2, YSelector.y2, e)}
                />
                <Selector x={(shape.x2 - shape.x1) / 2}
                          y={shape.y2 - shape.y1 + (shape.y1 < shape.y2 ? STROKE_WIDTH / 2 : -STROKE_WIDTH / 2)}
                          cursorClass="cursorResizeUpDown"
                          classes={classes}
                          stopPropagation={stopPropagation}
                          onStartSelectorDragging={e => onStartSelectorDragging(XSelector.xm, YSelector.y2, e)}
                />
                <Selector x={shape.x1 < shape.x2 ? -STROKE_WIDTH / 2 : STROKE_WIDTH / 2}
                          y={shape.y2 - shape.y1 + (shape.y1 < shape.y2 ? STROKE_WIDTH / 2 : -STROKE_WIDTH / 2)}
                          cursorClass={inverted ? 'cursorResizeUpperLeftLowerRight' : 'cursorResizeLowerLeftUpperRight'}
                          classes={classes}
                          stopPropagation={stopPropagation}
                          onStartSelectorDragging={e => onStartSelectorDragging(XSelector.x1, YSelector.y2, e)}
                />
                <Selector x={shape.x1 < shape.x2 ? -STROKE_WIDTH / 2 : STROKE_WIDTH / 2}
                          y={(shape.y2 - shape.y1) / 2}
                          cursorClass="cursorResizeLeftRight"
                          classes={classes}
                          stopPropagation={stopPropagation}
                          onStartSelectorDragging={e => onStartSelectorDragging(XSelector.x1, YSelector.ym, e)}
                />
                {!resizing &&
                <g
                    className={`cursorPointer ${classes.deleteButton}`}
                    transform={`translate(${Math.max(0, shape.x2 - shape.x1) + 10} ${Math.min(0, shape.y2 - shape.y1) + 5})`}
                    onClick={onDeleteButtonClicked}
                    onMouseDown={stopPropagation}
                    onDoubleClick={stopPropagation}
                >
                    <rect fill="transparent" x={-3} y={-3} width={30} height={30}/>
                    <path
                        d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
                    />
                    <path d="M0 0h24v24H0z" fill="none"/>
                </g>
                }
            </g>
            }

        </g>
    );
};

export default withStyles(styles)(ShapeView);



// WEBPACK FOOTER //
// ./src/components/ShapeView.tsx